import { useEffect, useRef, useState, type FormEvent } from 'react';
import { X } from 'lucide-react';
import type { Alerta } from '@tms/contratos';
import { MOTIVOS_JUSTIFICACION, MOTIVO_MIN_CARACTERES } from '../config';
import { useDatos } from '../state/Datos';

interface Props {
  alerta: Alerta | null;
  onCerrar: () => void;
  onConfirmar: (a: Alerta, motivo: string) => Promise<void>;
}

/** Diálogo de justificación del diseño: motivo de la lista + observaciones (mínimo 5 caracteres). */
export function DialogoJustificar({ alerta, onCerrar, onConfirmar }: Props) {
  const ref = useRef<HTMLDialogElement>(null);
  const [motivo, setMotivo] = useState('');
  const [nota, setNota] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);

  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    if (alerta && !d.open) {
      setMotivo('');
      setNota('');
      setError(null);
      d.showModal();
    } else if (!alerta && d.open) d.close();
  }, [alerta]);

  const enviar = async (e: FormEvent) => {
    e.preventDefault();
    if (!alerta) return;
    if (!motivo) return setError('Elegí un motivo.');
    if (nota.trim().length < MOTIVO_MIN_CARACTERES) return setError(`Las observaciones deben tener al menos ${MOTIVO_MIN_CARACTERES} caracteres.`);
    setEnviando(true);
    setError(null);
    try {
      await onConfirmar(alerta, `${motivo}: ${nota.trim()}`);
      onCerrar();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo guardar la justificación.');
    } finally {
      setEnviando(false);
    }
  };

  return (
    <dialog id="justify" className="dlg" ref={ref} onClose={onCerrar} aria-labelledby="justify-titulo">
      <form onSubmit={enviar} noValidate>
        <div className="dialog-head">
          <h2 id="justify-titulo">Justificar novedad</h2>
          <button type="button" className="icon" aria-label="Cerrar" onClick={onCerrar}>
            <X aria-hidden />
          </button>
        </div>
        <p>{alerta ? `${alerta.titulo} · ${alerta.patente ?? alerta.vehiculoIdMicrotrack ?? ''}` : ''}</p>
        <label htmlFor="reason">Motivo</label>
        <select id="reason" value={motivo} onChange={(e) => setMotivo(e.target.value)} required>
          <option value="">Seleccioná un motivo</option>
          {MOTIVOS_JUSTIFICACION.map((m) => (
            <option key={m}>{m}</option>
          ))}
        </select>
        <label htmlFor="note">Observaciones</label>
        <textarea
          id="note"
          rows={4}
          required
          minLength={MOTIVO_MIN_CARACTERES}
          placeholder="Agregá el contexto de la novedad"
          value={nota}
          onChange={(e) => setNota(e.target.value)}
        />
        {error && (
          <p className="error-msg" role="alert">
            {error}
          </p>
        )}
        <div className="form-actions">
          <button type="button" onClick={onCerrar}>
            Cancelar
          </button>
          <button className="primary" type="submit" disabled={enviando}>
            Guardar justificación
          </button>
        </div>
      </form>
    </dialog>
  );
}

/** Acciones sobre alertas compartidas por la bandeja, la pantalla de alertas y el detalle. */
export function useAccionesAlerta() {
  const { resolver, justificar } = useDatos();
  const [enJustificacion, setEnJustificacion] = useState<Alerta | null>(null);
  const dialogo = <DialogoJustificar alerta={enJustificacion} onCerrar={() => setEnJustificacion(null)} onConfirmar={justificar} />;
  return { resolver, pedirJustificacion: setEnJustificacion, dialogo };
}
